import { Component } from '@theme/component';
import { isDesktopBreakpoint, prefersReducedMotion } from '@theme/utilities';

/**
 * Accordion visual.
 *
 * Which `<details>` is open is entirely native (they share a `name`, so the
 * browser closes the previous one whenever a new one opens). This component
 * only keeps the desktop media stage and any per-item video in sync with
 * that state — see sections/accordion-visual.liquid and
 * blocks/_accordion-visual-item.liquid for the markup this reacts to.
 */
export class AccordionVisualComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) {
      for (const video of this.querySelectorAll('video')) {
        video.removeAttribute('autoplay');
        video.pause();
      }
      return;
    }

    for (const media of this.querySelectorAll(
      '.accordion-visual__media-item:not(.accordion-visual__media-item--active)',
    )) {
      media.querySelector('video')?.pause();
    }

    for (const details of this.querySelectorAll('.accordion-visual__details:not([open])')) {
      details.querySelector('.accordion-visual__panel-media video')?.pause();
    }
  }

  /**
   * Desktop only: prevents a click on the already-open item's own trigger
   * from closing it, so an item is only ever closed by opening another one
   * (no arrow icon there either — nothing invites a collapse). Mobile keeps
   * the native toggle, arrow included.
   * @param {MouseEvent} event
   */
  preventCloseActive(event) {
    if (!isDesktopBreakpoint()) return;

    // `currentTarget` would be `document` here (the framework's `on:click`
    // dispatch is one delegated listener on `document`); `target` is the
    // one attribute proxying makes point at the actual `on:click` element.
    const summary = event.target;
    if (!(summary instanceof HTMLElement)) return;

    const details = summary.closest('details');
    if (details?.open) event.preventDefault();
  }

  /**
   * Fires for both the `<details>` that just opened and whichever one the
   * browser auto-closed for it. Syncs that item's desktop stage media and
   * plays/pauses its video either way.
   * @param {Event} event
   */
  handleToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    const isOpen = details.open;
    const itemId = details.dataset.itemId;

    const media = this.querySelector(`.accordion-visual__media-item[data-item-id="${itemId}"]`);
    media?.classList.toggle('accordion-visual__media-item--active', isOpen);

    // Only the video actually visible at the current breakpoint should play;
    // the other copy (desktop stage vs. mobile in-panel) is always hidden via
    // CSS, so playing it too would just decode video no one sees.
    const isDesktop = isDesktopBreakpoint();
    this.#setPlaying(isDesktop ? media?.querySelector('video') : null, isOpen);
    this.#setPlaying(isDesktop ? null : details.querySelector('.accordion-visual__panel-media video'), isOpen);
  }

  /**
   * @param {Element | null | undefined} video
   * @param {boolean} shouldPlay
   */
  #setPlaying(video, shouldPlay) {
    if (!(video instanceof HTMLVideoElement)) return;

    if (shouldPlay && !prefersReducedMotion()) {
      video.play();
    } else {
      video.pause();
    }
  }
}

if (!customElements.get('accordion-visual-component')) {
  customElements.define('accordion-visual-component', AccordionVisualComponent);
}
